import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Loader2, ClipboardList, Brain, CheckCircle2, Clock, Mail,
  FileText, ChevronDown, ChevronUp, ArrowLeft, Sparkles,
  Receipt, FolderKanban,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const statusLabels = {
  pending: { label: "En attente", color: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  submitted: { label: "Repondu", color: "bg-[#0099CC]/10 text-[#0099CC]" },
  analyzed: { label: "Analyse", color: "bg-[#10B981]/10 text-[#10B981]" },
};

export default function QuestionnairesAdminPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API}/project-questionnaires`);
      setItems(res.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const triggerAnalysis = async (qid) => {
    setAnalyzing(qid);
    try {
      const res = await axios.post(`${API}/project-questionnaires/${qid}/analyze`);
      toast.success("Cahier des charges genere !");
      fetchAll();
      setExpanded(qid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur analyse IA");
    } finally { setAnalyzing(null); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="questionnaires-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-[#8B5CF6]" />
            Questionnaires projet
          </h1>
          <p className="text-[#94A3B8] mt-1">{items.length} questionnaire(s)</p>
        </div>
        <Link to="/app/leads">
          <Button variant="outline" className="border-[#2A3441] text-[#94A3B8] hover:text-white">
            <ArrowLeft className="h-4 w-4 mr-1.5" />Leads
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <ClipboardList className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun questionnaire</h3>
          <p className="text-[#94A3B8]">Envoyez un questionnaire depuis la page Leads.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(q => {
            const cfg = statusLabels[q.status] || statusLabels.pending;
            const isExpanded = expanded === q.id;
            const date = q.created_at ? new Date(q.created_at).toLocaleDateString("fr-FR") : "";
            const submittedDate = q.submitted_at ? new Date(q.submitted_at).toLocaleDateString("fr-FR") : "";

            return (
              <div key={q.id} className="bg-[#151B2B] border border-[#2A3441] rounded-xl overflow-hidden"
                data-testid={`questionnaire-card-${q.id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${cfg.color.split(' ')[0]}`}>
                        <ClipboardList className={`h-5 w-5 ${cfg.color.split(' ')[1]}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-semibold">{q.prospect_name}</p>
                          <Badge className={`${cfg.color} border-0 text-xs`}>{cfg.label}</Badge>
                          {q.lead_score != null && (
                            <Badge className={`border-0 text-xs ${q.lead_score >= 70 ? "bg-[#10B981]/10 text-[#10B981]" : q.lead_score >= 40 ? "bg-[#F59E0B]/10 text-[#F59E0B]" : "bg-[#EF4444]/10 text-[#EF4444]"}`}>
                              Score: {q.lead_score}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-[#64748b]">
                          <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{q.prospect_email}</span>
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Envoye: {date}</span>
                          {submittedDate && <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-[#10B981]" />Repondu: {submittedDate}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {q.status === "submitted" && (
                        <Button size="sm" onClick={() => triggerAnalysis(q.id)}
                          disabled={analyzing === q.id}
                          className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white h-8 text-xs"
                          data-testid={`analyze-btn-${q.id}`}>
                          {analyzing === q.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Brain className="h-3 w-3 mr-1" />}
                          Analyser (IA)
                        </Button>
                      )}
                      {q.status === "analyzed" && (
                        <Badge className="bg-[#10B981]/10 text-[#10B981] border-0 text-xs flex items-center gap-1">
                          <Sparkles className="h-3 w-3" />Cahier des charges genere
                        </Badge>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => setExpanded(isExpanded ? null : q.id)}
                        className="text-[#94A3B8] hover:text-white">
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Expanded: show responses + AI analysis */}
                {isExpanded && (
                  <div className="border-t border-[#2A3441] bg-[#0B0F19]/50 p-5 space-y-6 animate-fade-in">
                    {/* Responses */}
                    {q.responses && Object.keys(q.responses).length > 0 && (
                      <div>
                        <h4 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                          <FileText className="h-4 w-4 text-[#0099CC]" />Reponses du prospect
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(q.responses).map(([key, val]) => (
                            <div key={key} className="bg-[#151B2B] border border-[#2A3441] rounded-lg p-3">
                              <p className="text-[#64748b] text-xs capitalize mb-1">{key.replace(/_/g, " ")}</p>
                              <p className="text-white text-sm">{Array.isArray(val) ? val.join(", ") : val || "-"}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* AI Analysis */}
                    {q.ai_analysis && (
                      <div>
                        <h4 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                          <Brain className="h-4 w-4 text-[#8B5CF6]" />Cahier des charges (IA)
                        </h4>
                        <div className="bg-[#151B2B] border border-[#8B5CF6]/20 rounded-xl p-5 space-y-4">
                          {q.ai_analysis.resume_executif && (
                            <div className="bg-[#8B5CF6]/5 border border-[#8B5CF6]/20 rounded-lg p-4">
                              <p className="text-[#8B5CF6] text-xs font-semibold mb-1">Resume executif</p>
                              <p className="text-white text-sm leading-relaxed">{q.ai_analysis.resume_executif}</p>
                            </div>
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {q.ai_analysis.titre_projet && (
                              <div className="bg-[#0B0F19] rounded-lg p-3">
                                <p className="text-[#64748b] text-xs mb-1">Projet</p>
                                <p className="text-white text-sm font-medium">{q.ai_analysis.titre_projet}</p>
                              </div>
                            )}
                            {q.ai_analysis.estimation_budget && (
                              <div className="bg-[#0B0F19] rounded-lg p-3">
                                <p className="text-[#64748b] text-xs mb-1">Budget estime</p>
                                <p className="text-white text-sm font-medium">{q.ai_analysis.estimation_budget}</p>
                              </div>
                            )}
                            {q.ai_analysis.estimation_delai && (
                              <div className="bg-[#0B0F19] rounded-lg p-3">
                                <p className="text-[#64748b] text-xs mb-1">Delai estime</p>
                                <p className="text-white text-sm font-medium">{q.ai_analysis.estimation_delai}</p>
                              </div>
                            )}
                            {q.ai_analysis.priorite && (
                              <div className="bg-[#0B0F19] rounded-lg p-3">
                                <p className="text-[#64748b] text-xs mb-1">Priorite</p>
                                <p className="text-white text-sm font-medium capitalize">{q.ai_analysis.priorite}</p>
                              </div>
                            )}
                          </div>
                          {q.ai_analysis.pages_necessaires?.length > 0 && (
                            <div>
                              <p className="text-[#64748b] text-xs mb-2">Pages necessaires</p>
                              <div className="flex flex-wrap gap-1.5">
                                {q.ai_analysis.pages_necessaires.map((p, i) => (
                                  <Badge key={i} className="bg-[#0099CC]/10 text-[#0099CC] border-0 text-xs">{p}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {q.ai_analysis.fonctionnalites_requises?.length > 0 && (
                            <div>
                              <p className="text-[#64748b] text-xs mb-2">Fonctionnalites</p>
                              <div className="flex flex-wrap gap-1.5">
                                {q.ai_analysis.fonctionnalites_requises.map((f, i) => (
                                  <Badge key={i} className="bg-[#10B981]/10 text-[#10B981] border-0 text-xs">{f}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {q.ai_analysis.recommandations?.length > 0 && (
                            <div>
                              <p className="text-[#64748b] text-xs mb-2">Recommandations</p>
                              <ul className="space-y-1">
                                {q.ai_analysis.recommandations.map((r, i) => (
                                  <li key={i} className="text-[#94A3B8] text-sm flex items-start gap-2">
                                    <Sparkles className="h-3 w-3 text-[#F59E0B] mt-1 shrink-0" />{r}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-[#2A3441]">
                          <Button
                            disabled={actionLoading === q.id}
                            onClick={async () => {
                              setActionLoading(q.id);
                              try {
                                const res = await axios.post(`${API}/project-questionnaires/${q.id}/create-proposal`);
                                toast.success("Devis cree avec succes");
                                navigate("/app/propositions");
                              } catch { toast.error("Erreur creation devis"); }
                              finally { setActionLoading(null); }
                            }}
                            className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                            data-testid={`create-proposal-${q.id}`}
                          >
                            {actionLoading === q.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Receipt className="h-4 w-4 mr-2" />}
                            Creer un devis
                          </Button>
                          <Button
                            disabled={actionLoading === q.id}
                            onClick={async () => {
                              setActionLoading(q.id);
                              try {
                                await axios.post(`${API}/project-questionnaires/${q.id}/create-project`);
                                toast.success("Projet cree avec succes");
                                navigate("/app/projets");
                              } catch { toast.error("Erreur creation projet"); }
                              finally { setActionLoading(null); }
                            }}
                            className="bg-[#10B981] hover:bg-[#059669] text-white"
                            data-testid={`create-project-${q.id}`}
                          >
                            {actionLoading === q.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FolderKanban className="h-4 w-4 mr-2" />}
                            Lancer la production
                          </Button>
                        </div>
                      </div>
                    )}

                    {q.status === "pending" && (
                      <p className="text-[#64748b] text-sm italic">Le prospect n'a pas encore repondu au questionnaire.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
